import { useFetchBookingDetailsQuery, useLazyFetchBookingDetailsQuery } from '../../api/rtkQuery/fetchBookingDetailsApi'
import {
    isFailedBookingStatus,
    isInProgressBookingStatus,
    isSuccessBookingStatus
} from '../../utils/confirmation/bookingStatusUi'
import { useEffect, useMemo, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 5000
const MAX_POLLS = 10
const POLL_COUNT_STORAGE_KEY_PREFIX = 'confirmationPollCount:'

const isTerminalStatus = (status?: string) => isSuccessBookingStatus(status) || isFailedBookingStatus(status)
const isEmptyPnr = (pnr?: string | null) => !pnr || pnr.trim() === ''
const getPollCountStorageKey = (bookingId: string) => `${POLL_COUNT_STORAGE_KEY_PREFIX}${bookingId}`

export const hasCompletedPollingCycle = (wasFetching: boolean, isFetching: boolean) => wasFetching && !isFetching

export const getAsyncPollingDerivedState = ({
    isAsyncFlow,
    shouldUseIntervalPolling,
    pollCount,
    polledStatus,
    initialStatus
}: {
    isAsyncFlow: boolean
    shouldUseIntervalPolling: boolean
    pollCount: number
    polledStatus?: string
    initialStatus?: string
}) => {
    const reachedPollCap = isAsyncFlow && pollCount >= MAX_POLLS
    const hasTerminalPolledStatus = isTerminalStatus(polledStatus)
    const shouldForceInProgress = isAsyncFlow && reachedPollCap && !hasTerminalPolledStatus
    const isTimedOut = isAsyncFlow && reachedPollCap && !hasTerminalPolledStatus
    const isPolling = isAsyncFlow && shouldUseIntervalPolling && !hasTerminalPolledStatus
    const currentBookingStatus = isAsyncFlow
        ? isPolling && !polledStatus
            ? 'BOOKING_IN_PROGRESS'
            : shouldForceInProgress
              ? 'BOOKING_IN_PROGRESS'
              : polledStatus || initialStatus || ''
        : initialStatus || ''

    return {
        reachedPollCap,
        hasTerminalPolledStatus,
        shouldForceInProgress,
        isTimedOut,
        isPolling,
        currentBookingStatus
    }
}

const getStoredPollCount = (bookingId: string) => {
    if (typeof window === 'undefined' || !bookingId) return 0
    const rawCount = window.sessionStorage.getItem(getPollCountStorageKey(bookingId))
    const parsedCount = Number(rawCount || 0)
    if (!Number.isFinite(parsedCount) || parsedCount < 0) return 0
    return Math.min(parsedCount, MAX_POLLS)
}

const setStoredPollCount = (bookingId: string, count: number) => {
    if (typeof window === 'undefined' || !bookingId) return
    window.sessionStorage.setItem(getPollCountStorageKey(bookingId), String(Math.min(Math.max(count, 0), MAX_POLLS)))
}

const getStatusFromBookingDetails = (bookingDetails: any[]) => {
    if (!Array.isArray(bookingDetails) || bookingDetails.length === 0) {
        return undefined
    }

    if (bookingDetails.some(item => item?.bookingStatus === 'BOOKING_FAILED')) {
        return 'BOOKING_FAILED'
    }
    if (bookingDetails.some(item => item?.bookingStatus === 'BOOKING_IN_PROGRESS')) {
        return 'BOOKING_IN_PROGRESS'
    }
    if (bookingDetails.some(item => item?.bookingStatus === 'TICKET_PENDING')) {
        return 'TICKET_PENDING'
    }
    if (bookingDetails.some(item => item?.bookingStatus === 'BOOKING_SUCCESS')) {
        return 'BOOKING_SUCCESS'
    }

    return undefined
}

const mergePnrMap = (primaryMap: any, fallbackDetails: any[]) => {
    if (!Array.isArray(fallbackDetails) || fallbackDetails.length === 0) {
        return primaryMap
    }

    const safePrimaryMap = primaryMap || {}
    const mergedMap = { ...safePrimaryMap }

    fallbackDetails.forEach((item, index) => {
        const derivedKey = item?.journeyId != null ? String(item.journeyId) : String(index + 1)
        const existingItem = safePrimaryMap?.[derivedKey] || {}
        const fallbackPnr = item?.airlinePnr

        mergedMap[derivedKey] = {
            ...existingItem,
            // Keep primary values when already available; otherwise fall back to polled data.
            bookingStatusCode: existingItem?.bookingStatusCode || item?.bookingStatus || '',
            airlinePnr: isEmptyPnr(existingItem?.airlinePnr) ? fallbackPnr || '' : existingItem.airlinePnr,
            gdsPnr: existingItem?.gdsPnr || item?.gdsPnr || '',
            ticketNumber: existingItem?.ticketNumber || item?.ticketNumber || null
        }
    })

    return mergedMap
}

const isSameBookingStatusMap = (left: any, right: any) => {
    if (left === right) return true
    if (!left || !right) return false
    const leftKeys = Object.keys(left)
    const rightKeys = Object.keys(right)
    if (leftKeys.length !== rightKeys.length) return false
    return leftKeys.every(key => {
        const leftItem = left[key]
        const rightItem = right[key]
        return (
            leftItem?.bookingStatusCode === rightItem?.bookingStatusCode &&
            leftItem?.airlinePnr === rightItem?.airlinePnr &&
            leftItem?.gdsPnr === rightItem?.gdsPnr &&
            JSON.stringify(leftItem?.ticketNumber || null) === JSON.stringify(rightItem?.ticketNumber || null)
        )
    })
}

export const useBookConfirmPollingStatus = (
    id: string,
    initialStatus?: string,
    primaryBookingStatusDetailsMap?: any,
    isAsyncFlow = false
) => {
    const [pollCount, setPollCount] = useState(0)
    const [hasReceivedFirstPoll, setHasReceivedFirstPoll] = useState(!isAsyncFlow)
    const [shouldRunSingleFetchOnLoad, setShouldRunSingleFetchOnLoad] = useState(false)
    const [hasReachedTerminalStatus, setHasReachedTerminalStatus] = useState(false)
    const previousIsFetchingRef = useRef(false)
    const hasTriggeredSingleFetchRef = useRef(false)
    const stableMergedMapRef = useRef<any>(primaryBookingStatusDetailsMap)

    const shouldUseIntervalPolling = !!id && isAsyncFlow && pollCount < MAX_POLLS && !hasReachedTerminalStatus

    useEffect(() => {
        if (!isAsyncFlow || !id) {
            setPollCount(0)
            setHasReceivedFirstPoll(!isAsyncFlow)
            setShouldRunSingleFetchOnLoad(false)
            setHasReachedTerminalStatus(false)
            hasTriggeredSingleFetchRef.current = false
            previousIsFetchingRef.current = false
            return
        }
        const storedPollCount = getStoredPollCount(id)
        setPollCount(storedPollCount)
        setShouldRunSingleFetchOnLoad(storedPollCount >= MAX_POLLS)
        setHasReceivedFirstPoll(false)
        setHasReachedTerminalStatus(false)
        hasTriggeredSingleFetchRef.current = false
        previousIsFetchingRef.current = false
    }, [id, isAsyncFlow])

    const {
        data: intervalBookingDetailsData = [],
        isSuccess: hasIntervalPollResponse,
        isError: hasIntervalPollError,
        isFetching: isIntervalPollingRequestInFlight
    } = useFetchBookingDetailsQuery(
        { bookingId: id },
        {
            skip: !shouldUseIntervalPolling,
            pollingInterval: shouldUseIntervalPolling ? POLL_INTERVAL_MS : 0,
            refetchOnMountOrArgChange: true
        }
    )
    const [
        triggerSingleBookingDetailsFetch,
        { data: singleFetchBookingDetailsData = [], isSuccess: hasSingleFetchResponse, isError: hasSingleFetchError }
    ] = useLazyFetchBookingDetailsQuery()

    useEffect(() => {
        if (!isAsyncFlow || !id || !shouldRunSingleFetchOnLoad || hasTriggeredSingleFetchRef.current) {
            return
        }
        hasTriggeredSingleFetchRef.current = true
        triggerSingleBookingDetailsFetch({ bookingId: id })
    }, [id, isAsyncFlow, shouldRunSingleFetchOnLoad, triggerSingleBookingDetailsFetch])

    useEffect(() => {
        if (!isAsyncFlow || !id || !shouldUseIntervalPolling || hasReachedTerminalStatus) {
            return
        }
        const hasCompletedRequestCycle = hasCompletedPollingCycle(
            previousIsFetchingRef.current,
            isIntervalPollingRequestInFlight
        )
        if (hasCompletedRequestCycle) {
            setPollCount(prevCount => {
                const nextCount = Math.min(prevCount + 1, MAX_POLLS)
                setStoredPollCount(id, nextCount)
                return nextCount
            })
        }
        previousIsFetchingRef.current = isIntervalPollingRequestInFlight
    }, [id, isAsyncFlow, isIntervalPollingRequestInFlight, shouldUseIntervalPolling, hasReachedTerminalStatus])

    const bookingDetailsData =
        (singleFetchBookingDetailsData?.length ? singleFetchBookingDetailsData : intervalBookingDetailsData) || []

    useEffect(() => {
        if (!isAsyncFlow) {
            return
        }
        if (hasIntervalPollResponse || hasSingleFetchResponse || hasIntervalPollError || hasSingleFetchError) {
            setHasReceivedFirstPoll(true)
        }
    }, [hasIntervalPollResponse, hasSingleFetchResponse, hasIntervalPollError, hasSingleFetchError, isAsyncFlow])

    const polledStatus = getStatusFromBookingDetails(bookingDetailsData)

    useEffect(() => {
        if (!isAsyncFlow || !polledStatus) {
            return
        }
        if (isTerminalStatus(polledStatus) && !hasReachedTerminalStatus) {
            setHasReachedTerminalStatus(true)
        }
    }, [isAsyncFlow, polledStatus, hasReachedTerminalStatus])

    const { isTimedOut, isPolling, currentBookingStatus } = getAsyncPollingDerivedState({
        isAsyncFlow,
        shouldUseIntervalPolling,
        pollCount,
        polledStatus,
        initialStatus
    })
    const isPnrLoading = isAsyncFlow && isInProgressBookingStatus(currentBookingStatus) && !isTimedOut
    const mergedBookingStatusDetailsMap = useMemo(() => {
        if (!isAsyncFlow) return primaryBookingStatusDetailsMap
        const nextMap = mergePnrMap(primaryBookingStatusDetailsMap, bookingDetailsData)
        if (isSameBookingStatusMap(stableMergedMapRef.current, nextMap)) {
            return stableMergedMapRef.current
        }
        stableMergedMapRef.current = nextMap
        return nextMap
    }, [isAsyncFlow, primaryBookingStatusDetailsMap, bookingDetailsData])

    return {
        currentBookingStatus,
        isTimedOut,
        isPolling: isAsyncFlow ? isPolling : false,
        isPnrLoading,
        mergedBookingStatusDetailsMap,
        hasReceivedFirstPoll
    }
}
